import React, { useState, useEffect } from 'react';
import Button from './Button';

const CustomColumnModal = ({ isOpen, onClose, onCreateColumn, existingColumns = [], editingColumn = null }) => {
  const [columnName, setColumnName] = useState('');
  const [columnColor, setColumnColor] = useState('#6B7280');
  const [creating, setCreating] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (editingColumn) {
      setColumnName(editingColumn.name || '');
      setColumnColor(editingColumn.color || '#6B7280');
    } else {
      setColumnName('');
      setColumnColor('#6B7280');
    }
  }, [editingColumn]);

  const predefinedColors = [
    { color: null, name: 'No Color' },
    { color: '#6B7280', name: 'Gray' },
    { color: '#3B82F6', name: 'Blue' },
    { color: '#10B981', name: 'Green' },
    { color: '#F59E0B', name: 'Amber' },
    { color: '#EF4444', name: 'Red' },
    { color: '#8B5CF6', name: 'Purple' },
    { color: '#EC4899', name: 'Pink' },
    { color: '#06B6D4', name: 'Cyan' },
  ];

  const handleCreate = async () => {
    if (!columnName.trim()) return;
    
    setCreating(true);
    try {
      const columnData = { 
        name: columnName.trim(), 
        color: columnColor,
        order_index: editingColumn ? editingColumn.order_index : existingColumns.length 
      };
      
      if (editingColumn) {
        columnData.id = editingColumn.id;
      }
      
      await onCreateColumn(columnData);
      setColumnName('');
      setColumnColor('#6B7280');
      onClose();
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {editingColumn ? 'Edit Group' : 'Create Custom Group'}
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                {editingColumn ? 'Update group name and color' : 'Add a new group to organize your questions'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none p-2 rounded-full hover:bg-gray-100"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Group Name
              </label>
              <input
                type="text"
                value={columnName}
                onChange={(e) => setColumnName(e.target.value)}
                placeholder="e.g., Technical, Business, Follow-up..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleCreate()}
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Group Color
              </label>
              <div className="grid grid-cols-4 gap-2">
                {predefinedColors.map(({ color, name }) => (
                  <button
                    key={color || 'no-color'}
                    onClick={() => setColumnColor(color)}
                    className={`flex items-center justify-center h-10 rounded-lg transition-all border-2 ${
                      columnColor === color 
                        ? 'ring-2 ring-offset-2 ring-gray-400 border-gray-400' 
                        : 'hover:ring-2 hover:ring-gray-300 border-gray-200'
                    }`}
                    style={{ 
                      backgroundColor: color || 'transparent',
                      backgroundImage: color ? 'none' : 'linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)',
                      backgroundSize: '8px 8px',
                      backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
                    }}
                    title={name}
                  >
                    {columnColor === color && (
                      <svg className={`w-5 h-5 ${color ? 'text-white' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview
              </label>
              <div 
                className="p-4 rounded-lg border-2"
                style={{ 
                  backgroundColor: columnColor ? `${columnColor}10` : 'transparent',
                  borderColor: columnColor || '#d1d5db',
                  backgroundImage: columnColor ? 'none' : 'linear-gradient(45deg, #f9fafb 25%, transparent 25%), linear-gradient(-45deg, #f9fafb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f9fafb 75%), linear-gradient(-45deg, transparent 75%, #f9fafb 75%)',
                  backgroundSize: '8px 8px',
                  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
                }}
              >
                <h3 className="font-semibold text-gray-900 flex items-center">
                  {columnName || 'Group Name'}
                  <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full bg-white text-gray-700">
                    0
                  </span>
                </h3>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={onClose} size="sm">
                Cancel
              </Button>
              <Button 
                onClick={handleCreate} 
                disabled={!columnName.trim() || creating}
                size="sm"
              >
                {creating 
                  ? (editingColumn ? 'Updating...' : 'Creating...') 
                  : (editingColumn ? 'Update Group' : 'Create Group')
                }
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomColumnModal;

